import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { BehaviorSubject, EMPTY, Observable, catchError, forkJoin, mergeMap, of, switchMap, tap } from 'rxjs';
import { GroupService } from './group.service';
import { SocketService } from './socket.service';

@Injectable({
  providedIn: 'root'
})
export class TokenUsageService {

  private apiUrl = 'api';

  private groupUsageSubject = new BehaviorSubject<any[] | null>(null);
  groupUsage$ = this.groupUsageSubject.asObservable();
  private textMessageGroupUsageSubject = new BehaviorSubject<any[] | null>(null);
  textMessageGroupUsage$ = this.textMessageGroupUsageSubject.asObservable();
  private groupTokenHistorySubject = new BehaviorSubject<any[] | null>(null);
  tokenHistory$ = this.groupTokenHistorySubject.asObservable();
  private groupTextMessageHistorySubject = new BehaviorSubject<any[] | null>(null);
  groupTextMessageHistory$ = this.groupTextMessageHistorySubject.asObservable();
  private weeklyUsageSubject = new BehaviorSubject<any[] | null>(null);
  weeklyUsage$ = this.weeklyUsageSubject.asObservable();
  private weeklyTextMessageUsageSubject = new BehaviorSubject<any[] | null>(null);
  weeklyTextMessageUsage$ = this.weeklyTextMessageUsageSubject.asObservable();
  private monthlyUsageSubject = new BehaviorSubject<any[] | null>(null);
  monthlyUsage$ = this.monthlyUsageSubject.asObservable();
  private monthlyTextMessageUsageSubject = new BehaviorSubject<any[] | null>(null);
  monthlyTextMessageUsage$ = this.monthlyTextMessageUsageSubject.asObservable();
  private tokenBalanceSubject = new BehaviorSubject<number>(0.00);
  tokenBalance$ = this.tokenBalanceSubject.asObservable();
  private textMessageCreditBalanceSubject = new BehaviorSubject<number>(0.00);
  textMessageCreditBalance$ = this.textMessageCreditBalanceSubject.asObservable();

  groupId: any;
  pageSize = 500;
  textMessagesPageSize = 500;
  historyPageSize = 500;
  textMessageHistoryPageSize = 500;

  currentPage = 1;
  currentTextMessagesPage = 1;
  currentHistoryPage = 1;
  currentTextMessageHistoryPage = 1;
  isLoading = false;
  isLoadingTextMessages = false;
  isLoadingHistory = false;
  isLoadingTextMessageHistory = false;

  dateRangeOptions: any = {};

  constructor(private http: HttpClient, private groupService: GroupService, private socketService: SocketService) {
    this.setupRealTimeUpdates();

    /* 
    this.groupService.selectedGroup$.pipe(
      switchMap(selectedGroup => {

        if (selectedGroup) {
          this.groupId = selectedGroup.id;
          this.resetLoadParameters();
          return forkJoin([
            //this.loadGroupUsage(this.dateRangeOptions.startDate, this.dateRangeOptions.endDate),
            //this.loadGroupTextMessageUsage(this.dateRangeOptions.startDate, this.dateRangeOptions.endDate),
            //this.loadGroupTokenHistory(this.dateRangeOptions.startDate, this.dateRangeOptions.endDate),
            //this.loadGroupTextMessageHistory(this.dateRangeOptions.startDate, this.dateRangeOptions.endDate),
            //this.getWeeklyUsageByGroup(this.groupId),
            //this.getWeeklyTextMessageUsageByGroup(this.groupId),
            //this.getMonthlyUsageByGroup(this.groupId),
            //this.getMonthlyTextMessageUsageByGroup(this.groupId),
            this.getTokenBalanceForGroup(this.groupId),
            this.getTextMessageCreditBalanceForGroup(this.groupId),
          ]);
        }
        return EMPTY;
      })
    ).subscribe({
      next: (results) => {

      },
      error: (error) => {
        console.error('[TOKEN-USAGE-SERVICE] Error loading balances:', error);
      }
    });
  }
    
    */

    this.groupService.selectedGroup$.pipe(
      tap(selectedGroup => {
        if (selectedGroup) {
          this.groupId = selectedGroup.id;
          this.resetLoadParameters();

          // Extract balances directly from the group object
          const tokenBalance = parseFloat(selectedGroup.tokenBalance || 0);
          const textMessageBalance = parseFloat(selectedGroup.textMessageCredits || 0);

          this.tokenBalanceSubject.next(tokenBalance);
          this.textMessageCreditBalanceSubject.next(textMessageBalance);
        } else {
          // Reset balances when no group is selected
          this.tokenBalanceSubject.next(0);
          this.textMessageCreditBalanceSubject.next(0);
        }
      })
    ).subscribe();
  }

  get tokenBalanceSnapshot() {
    return this.tokenBalanceSubject.getValue();
  }


  get tokenTextMessageCreditSnapshot() {
    return this.tokenBalanceSubject.getValue();
  }


  private setupRealTimeUpdates(): void {

    this.socketService.listen('tokenBalanceChanged', 'token-usage-service').subscribe((newBalance) => {
      this.tokenBalanceSubject.next(newBalance.balance);
    });

    this.socketService.listen('textMessageCreditBalanceChanged', 'token-usage-service').subscribe((newBalance) => {
      this.textMessageCreditBalanceSubject.next(newBalance.balance);
    });

  }

  private setDateRangeOptions() {
    const defaultEndDate = moment.utc();
    let startDate, endDate;


    startDate = moment.utc().subtract(1, 'week');
    endDate = defaultEndDate;

    // Ensure startDate is not greater than endDate
    while (startDate.isAfter(endDate)) {
      startDate = endDate.clone().subtract(1, 'day');
    }

    // Ensure dates are not in the future
    const today = moment.utc();
    if (startDate.isAfter(today)) {
      startDate = today;
    }
    if (endDate.isAfter(today)) {
      endDate = today;
    }

    this.dateRangeOptions = { startDate, endDate };
  }

  resetLoadParameters() {
    this.currentPage = 1;
    this.currentHistoryPage = 1;
    this.groupUsageSubject.next([]);
    this.textMessageGroupUsageSubject.next([]);
    this.groupTokenHistorySubject.next([]);
    this.groupTextMessageHistorySubject.next([]);
    this.setDateRangeOptions();
  }

  loadGroupUsage(startDate: moment.Moment, endDate: moment.Moment): Observable<any[]> {
    if (this.isLoading) {
      return EMPTY;
    }
    this.isLoading = true;
    return this.getUsageByGroup(this.groupId, this.currentPage, this.pageSize, startDate, endDate).pipe(
      mergeMap((res: any) => {
        const items = res.data;
        const current = this.groupUsageSubject.getValue();
        const updated = current ? [...current, ...items] : items;
        this.groupUsageSubject.next(updated);
        if (items.length === this.pageSize) {
          this.currentPage++;
          this.isLoading = false;
          return this.loadGroupUsage(startDate, endDate);
        } else {
          this.isLoading = false;
          return of(updated);
        }
      }),
      catchError(error => {
        this.isLoading = false;
        return of([]);
      })
    );
  }


  loadGroupTextMessageUsage(startDate: moment.Moment, endDate: moment.Moment): Observable<any[]> {
    if (this.isLoadingTextMessages) {
      return EMPTY;
    }
    this.isLoadingTextMessages = true;
    return this.getTextMessageUsageByGroup(this.groupId, this.currentTextMessagesPage, this.textMessagesPageSize, startDate, endDate).pipe(
      mergeMap((res: any) => {
        const items = res.data;
        const current = this.textMessageGroupUsageSubject.getValue();
        const updated = current ? [...current, ...items] : items;
        this.textMessageGroupUsageSubject.next(updated);
        if (items.length === this.pageSize) {
          this.currentTextMessagesPage++;
          this.isLoadingTextMessages = false;
          return this.loadGroupTextMessageUsage(startDate, endDate);
        } else {
          this.isLoadingTextMessages = false;
          return of(updated);
        }
      }),
      catchError(error => {
        this.isLoadingTextMessages = false;
        return of([]);
      })
    );
  }

  loadGroupTokenHistory(startDate: moment.Moment, endDate: moment.Moment): Observable<any[]> {
    if (this.isLoadingHistory) {
      return EMPTY;
    }
    this.isLoadingHistory = true;
    return this.getTokenHistoryByGroup(this.groupId, this.currentHistoryPage, this.historyPageSize, startDate, endDate).pipe(
      mergeMap((res: any) => {
        const items = res.data;
        const current = this.groupTokenHistorySubject.getValue();
        const updated = current ? [...current, ...items] : items;
        this.groupTokenHistorySubject.next(updated);
        if (items.length === this.historyPageSize) {
          this.currentHistoryPage++;
          this.isLoadingHistory = false;
          return this.loadGroupTokenHistory(startDate, endDate);
        } else {
          this.isLoadingHistory = false;
          return of(updated);
        }
      }),
      catchError(error => {
        this.isLoadingHistory = false;
        return of([]);
      })
    );
  }


  loadGroupTextMessageHistory(startDate: moment.Moment, endDate: moment.Moment): Observable<any[]> {
    if (this.isLoadingTextMessageHistory) {
      return EMPTY;
    }
    this.isLoadingTextMessageHistory = true;
    return this.getTextMessageHistoryByGroup(this.groupId, this.currentTextMessageHistoryPage, this.textMessageHistoryPageSize, startDate, endDate).pipe(
      mergeMap((res: any) => {
        const items = res.data;
        const current = this.groupTextMessageHistorySubject.getValue();
        const updated = current ? [...current, ...items] : items;
        this.groupTextMessageHistorySubject.next(updated);
        if (items.length === this.historyPageSize) {
          this.currentTextMessageHistoryPage++;
          this.isLoadingTextMessageHistory = false;
          return this.loadGroupTextMessageHistory(startDate, endDate);
        } else {
          this.isLoadingTextMessageHistory = false;
          return of(updated);
        }
      }),
      catchError(error => {
        this.isLoadingTextMessageHistory = false;
        return of([]);
      })
    );
  }

  getUsageByGroup(groupId: string, page: number, pageSize: number, startDate: moment.Moment | string, endDate: moment.Moment | string): Observable<any[]> {
    const token = localStorage.getItem('token');
    startDate = encodeURIComponent(moment.utc(startDate).startOf('day').toISOString());
    endDate = encodeURIComponent(moment.utc(endDate).endOf('day').toISOString());
    return this.http.get<any[]>(`${this.apiUrl}/usage/group/${groupId}?page=${page}&pageSize=${pageSize}&startDate=${startDate}&endDate=${endDate}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }


  getTextMessageUsageByGroup(groupId: string, page: number, pageSize: number, startDate: moment.Moment | string, endDate: moment.Moment | string): Observable<any[]> {
    const token = localStorage.getItem('token');
    startDate = encodeURIComponent(moment.utc(startDate).startOf('day').toISOString());
    endDate = encodeURIComponent(moment.utc(endDate).endOf('day').toISOString());
    return this.http.get<any[]>(`${this.apiUrl}/usage/text-message/group/${groupId}?page=${page}&pageSize=${pageSize}&startDate=${startDate}&endDate=${endDate}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  getTokenHistoryByGroup(groupId: string, page: number, pageSize: number, startDate: moment.Moment | string, endDate: moment.Moment | string): Observable<any[]> {
    const token = localStorage.getItem('token');
    startDate = encodeURIComponent(moment.utc(startDate).startOf('day').toISOString());
    endDate = encodeURIComponent(moment.utc(endDate).endOf('day').toISOString());
    return this.http.get<any[]>(`${this.apiUrl}/balance/history/group/${groupId}?page=${page}&pageSize=${pageSize}&startDate=${startDate}&endDate=${endDate}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  getTextMessageHistoryByGroup(groupId: string, page: number, pageSize: number, startDate: moment.Moment | string, endDate: moment.Moment | string): Observable<any[]> {
    const token = localStorage.getItem('token');
    startDate = encodeURIComponent(moment.utc(startDate).startOf('day').toISOString());
    endDate = encodeURIComponent(moment.utc(endDate).endOf('day').toISOString());
    return this.http.get<any[]>(`${this.apiUrl}/balance/text-message/history/group/${groupId}?page=${page}&pageSize=${pageSize}&startDate=${startDate}&endDate=${endDate}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  getWeeklyUsageByGroup(groupId: string): Observable<any[]> {
    const token = localStorage.getItem('token');
    return this.http.get<any[]>(`${this.apiUrl}/usage/week/group/${groupId}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(tap((result: any) => this.weeklyUsageSubject.next(result.total)));
  }


  getWeeklyTextMessageUsageByGroup(groupId: string): Observable<any[]> {
    const token = localStorage.getItem('token');
    return this.http.get<any[]>(`${this.apiUrl}/usage/text-message/week/group/${groupId}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(tap((result: any) => this.weeklyTextMessageUsageSubject.next(result.total)));
  }

  getMonthlyUsageByGroup(groupId: string): Observable<any[]> {
    const token = localStorage.getItem('token');
    return this.http.get<any[]>(`${this.apiUrl}/usage/month/group/${groupId}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(tap((result: any) => this.monthlyUsageSubject.next(result.total)));
  }

  getMonthlyTextMessageUsageByGroup(groupId: string): Observable<any[]> {
    const token = localStorage.getItem('token');
    return this.http.get<any[]>(`${this.apiUrl}/usage/text-message/month/group/${groupId}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(tap((result: any) => this.monthlyTextMessageUsageSubject.next(result.total)));
  }


  getTokenBalanceForGroup(groupId: string): Observable<any[]> {
    const token = localStorage.getItem('token');
    return this.http.get<any[]>(`${this.apiUrl}/balance/group/${groupId}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(tap((result: any) => this.tokenBalanceSubject.next(result.balance)));
  }


  getTextMessageCreditBalanceForGroup(groupId: string): Observable<any[]> {

    const token = localStorage.getItem('token');
    return this.http.get<any[]>(`${this.apiUrl}/balance/text-message/group/${groupId}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      tap((result: any) => {
        this.textMessageCreditBalanceSubject.next(result.balance);
      }),
      catchError((error) => {
        console.error('[TOKEN-USAGE-SERVICE] Error fetching text message credit balance:', {
          error,
          errorMessage: error?.message,
          errorStatus: error?.status,
          groupId
        });
        // Set balance to 0 on error and return empty array to match return type
        this.textMessageCreditBalanceSubject.next(0);
        return of([]);
      })
    );
  }


}
